import { AutoAwesome, Event } from '@mui/icons-material';
import { ABTestType, CampaignChannels } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';
import { CampaignStateTypes } from '../../../customCampaignReducer';
import DetailItem from 'domains/core/components/DetailItem';
import { formatDateTime } from 'domains/core/components/DateRangeText/DateRangeText';
import { Stack, Tooltip, Typography } from '@mui/material';
import { getIsRecurrenceEnabled, getRecurrenceLabel } from 'domains/campaigns/utils';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import ABTestSlider from 'domains/campaigns/components/ABTestSlider';
import ABTestLabelBox from 'domains/campaigns/components/ABTestLabelBox';

type Props = {
    campaignState: CampaignStateTypes;
    navigateToABTestScheduling?: (emailIndex: number) => void;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignPublishStep = ({
    campaignState,
    navigateToABTestScheduling,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const isDirectMailSend = campaignState.sends[0].channel === CampaignChannels.DIRECT_MAIL;
    const isABTestingEnabled = !!campaignState.abTestConfig && !!campaignState.abTestConfig.testOn;

    const guidingText = `Take this moment to confirm the details of your Curation. ${
        isDirectMailSend
            ? 'Once the Curation is published, the files will start printing the business day after the schedule date. '
            : ''
    }${
        isABTestingEnabled
            ? 'You can always cancel upcoming sends, but cannot take back ones that have been sent.'
            : 'While you can always cancel upcoming sends, please note that you cannot take back any that have already been sent.'
    }`;

    const tooltipMessage = 'Click to edit';

    const reEntryDimension = campaignState.reentryDetails?.dimension === 'hour' ? 'hours' : 'days';
    const isABTestingSendTime = campaignState.abTestConfig?.testOn === ABTestType.SEND_TIME;

    // For regular campaigns or content A/B testing
    const scheduledTimestamp = campaignState.sends[0].scheduledTimestamp;
    const formattedScheduledTimestamp = formatDateTime(scheduledTimestamp);
    const recurrenceFrequency =
        campaignState.sends[0].channel === CampaignChannels.EMAIL ? campaignState.sends[0].recurrenceFrequency : null;
    const isRecurringCampaign = getIsRecurrenceEnabled(recurrenceFrequency);
    const recurrenceLabel = getRecurrenceLabel(recurrenceFrequency, scheduledTimestamp);
    const recurrenceLabelForDisplay = recurrenceLabel.charAt(0).toUpperCase() + recurrenceLabel.slice(1);

    // For send time A/B testing - get both send times
    const emailAScheduledTimestamp = campaignState.sends[0]?.scheduledTimestamp;
    const emailBScheduledTimestamp = campaignState.sends[1]?.scheduledTimestamp;
    const formattedEmailATimestamp = formatDateTime(emailAScheduledTimestamp);
    const formattedEmailBTimestamp = formatDateTime(emailBScheduledTimestamp);

    // A/B Testing helpers
    const getABTestTypeDisplayName = (type: ABTestType) => (type === ABTestType.CONTENT ? 'Content' : 'Send time');

    // Test end information for 50% split
    const is50PercentSplit = campaignState.abTestConfig?.testPercentage === 50;
    const testEndsOn = campaignState.abTestConfig?.testEndsOn;
    const formattedTestEndsOn = testEndsOn ? formatDateTime(testEndsOn) : '';

    return (
        <FormLayout header="last step!" stepName="publish" icon={<AutoAwesome />} guidingText={guidingText}>
            <Stack spacing={4}>
                {/* A/B Testing Section - Only show when A/B testing is enabled */}
                {isABTestingEnabled && (
                    <DetailItem
                        header="A/B Testing"
                        description={getABTestTypeDisplayName(campaignState.abTestConfig!.testOn!)}
                    />
                )}

                {/* Test Size Section - Only show when A/B testing is enabled */}
                {isABTestingEnabled && (
                    <DetailItem
                        header="Test Size"
                        description={campaignState.abTestConfig.testPercentage + '% audience'}
                        footer={
                            <ABTestSlider
                                segmentId={campaignState.sends[0].segmentId}
                                splitPercentage={campaignState.abTestConfig.testPercentage as 50 | 100}
                            />
                        }
                    />
                )}

                {/* Scheduled Section */}
                <DetailItem
                    header="Scheduled"
                    description={
                        <Stack>
                            {isABTestingSendTime ? (
                                <Stack spacing={1}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" alignItems="center">
                                            <ABTestLabelBox label="A" size="small" />
                                            <Typography variant="body1">{formattedEmailATimestamp}</Typography>
                                        </Stack>
                                        <Tooltip title="Click to edit Email A schedule">
                                            <Event
                                                onClick={() => {
                                                    if (navigateToABTestScheduling) {
                                                        navigateToABTestScheduling(0);
                                                    } else {
                                                        setActiveSendIndex(0);
                                                        setActiveStep(CustomCampaignSteps.SCHEDULE_CURATION);
                                                    }
                                                }}
                                                sx={{ cursor: 'pointer', fontSize: 20 }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" alignItems="center">
                                            <ABTestLabelBox label="B" size="small" />
                                            <Typography variant="body1">{formattedEmailBTimestamp}</Typography>
                                        </Stack>
                                        <Tooltip title="Click to edit Email B schedule">
                                            <Event
                                                onClick={() => {
                                                    if (navigateToABTestScheduling) {
                                                        navigateToABTestScheduling(1);
                                                    } else {
                                                        setActiveSendIndex(1);
                                                        setActiveStep(CustomCampaignSteps.SCHEDULE_CURATION);
                                                    }
                                                }}
                                                sx={{ cursor: 'pointer', fontSize: 20 }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            ) : (
                                <Typography variant="body1">{formattedScheduledTimestamp}</Typography>
                            )}
                            {isRecurringCampaign && (
                                <Typography variant="body2" sx={{ marginBottom: '16px' }}>
                                    {recurrenceLabelForDisplay}
                                </Typography>
                            )}
                            {is50PercentSplit && (
                                <Stack spacing={0} sx={{ marginTop: 1 }}>
                                    {testEndsOn && (
                                        <Typography variant="body2" color="text.secondary">
                                            Test ends on {formattedTestEndsOn}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        Winner will be <strong>manually</strong> sent to remaining 50%
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    }
                    descriptionIcon={
                        !isABTestingSendTime ? (
                            <Tooltip title={tooltipMessage}>
                                <Event onClick={() => setActiveStep(CustomCampaignSteps.SCHEDULE_CURATION)} />
                            </Tooltip>
                        ) : undefined
                    }
                />

                {/* Contacts Re-Enter Section - Only show when not A/B testing */}
                {campaignState?.reentryDetails && campaignState.reentryDetails.value && !isABTestingEnabled && (
                    <DetailItem
                        header="Contacts Re-Enter"
                        description={`${campaignState.reentryDetails.value} ${reEntryDimension} after last send`}
                    />
                )}
            </Stack>
        </FormLayout>
    );
};

export default CustomCampaignPublishStep;
